import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Banknote, Loader2, MapPin, Users } from "lucide-react";

import { fmtPeso } from "../../utils/formatters";
import { riderApi } from "../services/riderApi";
import RiderWorkHoldNotice from "./RiderWorkHoldNotice";

// WP8 Run remittance, Rider side (target-v2).
//
// The same boundary WP7 drew for parcels, drawn again for cash: presenting is not
// settling. The Rider says "here is the trip's cash"; authorized staff count it
// on their own screen, and until they do every peso here is still the Rider's
// accountability.
//
// There is deliberately no amount field. The total is the sum of the collections
// the server locks at submit time, not something a Rider types, because a Rider
// who could state the amount could state a smaller one. The figure below is shown
// so they can check it against what is in their bag before walking over - and if
// this screen is stale, the command refuses rather than accepting the number the
// screen happened to show.
//
// There is also no "mark as remitted" control, no receiver field, and no way to
// record a receipt from this side.
//
// The screen is read one-handed, often outdoors, often in a hurry. So the amount
// is the largest thing on it, the per-order breakdown is there to be checked
// against a bag rather than admired, and every refusal says what to do next.

const EMERGENCY_REASONS = Object.freeze([
  ["VEHICLE_INOPERABLE", "My vehicle cannot continue"],
  ["MEDICAL_EMERGENCY", "Medical emergency"],
  ["SAFETY_EMERGENCY", "Safety emergency"],
  ["DEVICE_FAILURE", "My device failed"],
  ["RIDER_UNABLE_TO_CONTINUE", "I cannot continue the trip"],
  ["OTHER", "Something else"],
]);

const KIND_LABEL = Object.freeze({
  CASH_IN_CUSTODY: "Take the cash to",
  PARCEL_IN_CUSTODY: "Take the parcels to",
});


function DestinationCard({ destination }) {
  const place = destination?.destination;
  if (!place) return null;
  const { redirected, committedDestination, redirection, receivers } = destination;
  return (
    <li className="rounded-xl border p-3 dark:border-white/10">
      <p className="text-xs text-neutral-500">
        {KIND_LABEL[destination.obligationKind] || "Take this to"}
      </p>
      <p className="mt-0.5 flex items-start gap-2 font-semibold">
        <MapPin className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        {place.label}
      </p>
      <p className="mt-1 pl-6 text-sm text-neutral-500">{place.formattedAddress}</p>
      {redirected ? (
        <p className="mt-2 rounded-lg bg-amber-50 p-2 text-sm text-amber-900 dark:bg-amber-500/10 dark:text-amber-200">
          Changed from {committedDestination?.label}. {redirection?.reason}
        </p>
      ) : null}
      <p className="mt-2 flex items-start gap-2 pl-0 text-xs text-neutral-500">
        <Users className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {receivers?.length
          ? `${receivers.map((person) => person.name).filter(Boolean).join(", ")} can receive this`
          : "Ask at the counter who is authorized to receive this"}
      </p>
    </li>
  );
}

// A trip's cash is handed over once, at the store, after the deliveries (owner
// decision 2026-09-26). While deliveries remain this panel only shows what the
// Rider is carrying; the one mid-trip hand-over is the "I cannot finish this
// trip" emergency, folded away. An early normal hand-over used to strand every
// later stop's cash, because a trip has exactly one hand-over.
export default function RiderRemittancePanel({ businessId, runId, runVersion = null, disabled = false, onChanged, deliveriesRemaining = false }) {
  const [preview, setPreview] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [holds, setHolds] = useState(null);
  const [emergencyReason, setEmergencyReason] = useState("VEHICLE_INOPERABLE");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [handedOver, setHandedOver] = useState(null);

  const load = useCallback(async () => {
    if (!businessId || !runId) return;
    setLoading(true);
    setError("");
    // Three independent reads; no reason for the Rider to wait for them in turn.
    const [outstanding, places, workHolds] = await Promise.all([
      riderApi.previewRunRemittance(businessId, runId).catch(() => null),
      riderApi.getSettlementDestinations(businessId, runId).catch(() => null),
      riderApi.getMyWorkHolds(businessId).catch(() => null),
    ]);
    setPreview(outstanding);
    setDestinations(places?.destinations || []);
    setHolds(workHolds || null);
    setLoading(false);
  }, [businessId, runId]);

  // Reload whenever the trip changes. Cash collected at a stop used to stay
  // invisible here ("You are carrying ₱0.00") until the page was reopened,
  // while Finish trip already listed it as still to hand over.
  useEffect(() => { load(); }, [load, runVersion]);

  // Derived during render rather than mirrored into state: these are views of
  // `preview`, and an effect that copied them would just be a second source of
  // truth that can disagree with the first.
  const carryingCents = Number(preview?.expectedAmountCents || 0);
  const collections = useMemo(() => preview?.items || [], [preview]);
  const nothingToHandOver = carryingCents <= 0;
  const effectiveMode = deliveriesRemaining ? "emergency" : "remit";
  const FormShell = deliveriesRemaining ? "details" : "div";

  const submit = useCallback(async () => {
    setBusy(effectiveMode);
    setError("");
    try {
      const response = effectiveMode === "emergency"
        ? await riderApi.submitEmergencyCashRecovery(businessId, runId, {
          emergencyReasonCode: emergencyReason,
          notes: note || undefined,
        })
        : await riderApi.submitRunRemittance(businessId, runId, {
          // An agreement check, not an input. If this screen is stale the command
          // refuses, rather than letting cash change hands against a number
          // nobody else agrees with.
          acknowledgedAmountCents: carryingCents,
          notes: note || undefined,
        });
      setHandedOver(response);
      setNote("");
      await load();
      if (onChanged) await onChanged();
    } catch (submitError) {
      setError(submitError?.response?.data?.message || submitError?.message
        || "That did not record. Check the amount on screen and try again.");
    } finally {
      setBusy("");
    }
  }, [effectiveMode, businessId, runId, emergencyReason, note, carryingCents, load, onChanged]);

  // Nothing collected yet on a trip still delivering: nothing to show.
  if (!loading && deliveriesRemaining && nothingToHandOver && !handedOver) return null;

  if (loading) {
    return (
      <section className="rounded-2xl border p-5 dark:border-white/10">
        <div className="grid min-h-24 place-items-center">
          <Loader2 className="h-6 w-6 animate-spin" aria-label="Loading trip cash" />
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border p-5 dark:border-white/10">
      <header>
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Banknote className="h-5 w-5" aria-hidden="true" /> Trip cash
        </h2>
        <p className="mt-1 max-w-prose text-sm text-neutral-500">
          {deliveriesRemaining
            ? "Keep it safe. You hand all of it over at the store once your deliveries are done."
            : "Hand what you collected to the counter. Staff count it and confirm — until they do, it is still yours."}
        </p>
      </header>

      <RiderWorkHoldNotice holds={holds} className="mt-4" />

      {handedOver ? (
        <div className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50 p-3 dark:border-emerald-500/40 dark:bg-emerald-500/10">
          <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
            Handed over for counting
          </p>
          <p className="mt-1 max-w-prose text-sm text-emerald-900/90 dark:text-emerald-200/90">
            You presented {fmtPeso(handedOver.remittance?.expectedAmountCents)} from{' '}
            {handedOver.contributorCount || 0} collection(s). It is not settled until staff
            confirm what they count.
          </p>
        </div>
      ) : null}

      {/* The one loud thing on the screen. A Rider checks this against the bag in
          their hand, so it is set large and the label stays quiet. */}
      <p className="mt-5 text-sm text-neutral-500">You are carrying</p>
      <p className="text-4xl font-semibold tabular-nums tracking-tight">{fmtPeso(carryingCents)}</p>
      <p className="mt-1 text-sm text-neutral-500">
        from {collections.length} collection{collections.length === 1 ? '' : 's'} on this trip
      </p>

      {collections.length ? (
        <ul className="mt-3 divide-y rounded-xl border dark:divide-white/10 dark:border-white/10">
          {collections.map((item) => (
            <li key={item.codRecordId} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <span className="truncate">{item.orderReference || `Order #${item.shipmentId}`}</span>
              <span className="shrink-0 font-semibold tabular-nums">
                {fmtPeso(item.outstandingAmountCents)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {!deliveriesRemaining && destinations.length ? (
        <ul className="mt-4 grid gap-2">
          {destinations.map((destination) => (
            <DestinationCard key={destination.obligationKind} destination={destination} />
          ))}
        </ul>
      ) : null}

      {nothingToHandOver ? (
        <p className="mt-4 rounded-xl border border-dashed p-4 text-sm text-neutral-500">
          Nothing to hand over. Every collection on this trip has been remitted or returned.
        </p>
      ) : (
        <FormShell
          className="mt-4 rounded-xl border p-4 dark:border-white/10"
          {...(deliveriesRemaining ? { "data-testid": "trip-cash-emergency" } : {})}
        >
          {deliveriesRemaining ? (
            <summary className="flex min-h-11 cursor-pointer list-none items-center text-sm font-semibold">
              Can&apos;t finish this trip? Hand over the cash now
            </summary>
          ) : null}

          {effectiveMode === "emergency" ? (
            <div className="mt-3">
              <label className="text-sm font-semibold" htmlFor="rider-emergency-cash-reason">
                Why can you not finish?
              </label>
              <select
                id="rider-emergency-cash-reason"
                value={emergencyReason}
                onChange={(event) => setEmergencyReason(event.target.value)}
                className="mt-2 min-h-11 w-full rounded-xl border bg-transparent px-3"
              >
                {EMERGENCY_REASONS.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <p className="mt-2 max-w-prose text-sm text-neutral-500">
                This hands over the same cash early. It is not recorded as a failed delivery
                against anyone&apos;s parcel.
              </p>
            </div>
          ) : null}

          <label className="mt-3 block text-sm font-semibold" htmlFor="rider-remittance-note">
            Note <span className="font-normal text-neutral-500">(optional)</span>
          </label>
          <input
            id="rider-remittance-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            maxLength={1000}
            className="mt-2 min-h-11 w-full rounded-xl border bg-transparent px-3"
          />

          <button
            type="button"
            onClick={submit}
            disabled={disabled || Boolean(busy)}
            className="mt-3 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-sky-700 px-4 font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50 sm:w-auto"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {busy ? "Recording…" : deliveriesRemaining ? `I cannot finish - hand over ${fmtPeso(carryingCents)} now` : `Hand over ${fmtPeso(carryingCents)}`}
          </button>
          <p className="mt-2 max-w-prose text-xs text-neutral-500">
            Staff confirm what they physically count. Nothing is settled or reconciled here.
          </p>
        </FormShell>
      )}

      {error ? (
        <p role="alert" className="mt-4 flex items-start gap-2 rounded-xl bg-rose-50 p-3 text-sm text-rose-800 dark:bg-rose-500/10 dark:text-rose-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" /> {error}
        </p>
      ) : null}
    </section>
  );
}
